import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Button, FormControl, Grid, InputAdornment, InputLabel, MenuItem, Select,
  SelectChangeEvent, Skeleton, TextField, useTheme, Checkbox, Chip, ListItemText,
} from '@mui/material';
import RolesTable from './RolesTable';
import { ModulePermission, Persona } from '../../../../app/services/api/generated';
import { SearchLg, Trophy01 } from '../../../../assets';
import { EmptyTable } from '../../../../components/shared/EmptyTable';

const inputStyling = {
  width: '320px',
  '& .MuiInputBase-root': {
    height: '40px',
  },
};

export const transformForTranslation = (name: string) => name.replace(/\s+/g, '').replace(/^[A-Z]/, (char) => char.toLowerCase());

const RolesBox = (props: { personas: Persona[], modulePermissions: ModulePermission[], loadData: () => void, isLoading: boolean, onCreate: () => void }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const theme = useTheme();
  const [moduleFilter, setModuleFilter] = useState<string[]>([]);
  const { t } = useTranslation('manageRoles');
  const [transformedData, setTransformedData] = useState<Persona[]>([]);

  useEffect(() => {
    setTransformedData(props.personas ?? []);
  }, [props.personas]);

  const handleSearch = (personaName: string) => {
    setSearchTerm(personaName);
    let filteredData = [...props.personas];
    if (personaName && personaName !== '') {
      filteredData = props.personas.filter(x =>
        x.description?.toLowerCase().includes(personaName.toLowerCase()));
    }
    if (moduleFilter.length > 0) {
      filteredData = filteredData.filter(x => x.modules?.some(m => moduleFilter.includes(m)));
    }
    setTransformedData(filteredData);
  };

  const handleModuleSelect = (event: SelectChangeEvent<string[]>) => {
    const { target: { value } } = event;

    const isSingleItemSelected = typeof value === 'string';
    const isAllSelected =
      typeof value === 'string' && value === '' ||
      typeof value !== 'string' && value.some(x => x === '');

    const selectedModules = isAllSelected ? []
      : isSingleItemSelected ? value.split(',') : value;

    if (selectedModules.includes('ALL')) {
      setModuleFilter(['ALL']);
    } else {
      setModuleFilter(selectedModules);
    }

    let filteredData = [...props.personas];

    if (searchTerm && searchTerm !== '') {
      filteredData = props.personas.filter(x =>
        x.description?.toLowerCase().includes(searchTerm.toLowerCase()));
    }

    if (selectedModules.length > 0 && !selectedModules.includes('ALL')) {
      filteredData = filteredData.filter(persona => {
        if (!persona.modules || persona.modules.length === 0) return false;

        return selectedModules.every(x => persona.modules?.includes(x));
      });
    }

    setTransformedData(filteredData);
  };

  const handleClear = () => {
    setSearchTerm('');
    setModuleFilter([]);
    setTransformedData(props.personas);
  };

  return (
    <Grid item
      mt={5}
      xs={12}
    >
      <Box paddingY={5}
        sx={{ display: 'flex', gap: 4, width: '100%' }}>
        <TextField
          onChange={(event) => handleSearch(event.target.value)}
          size='small'
          placeholder={t('search')}
          value={searchTerm}
          sx={{ ...inputStyling }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchLg />
              </InputAdornment>
            ),
          }}
        />
        <FormControl
          variant="outlined"
          size='small'
          sx={{ ...inputStyling }}
        >
          <InputLabel
            sx={{
              '&.MuiInputLabel-shrink': {
                top: 0,
              },
            }}
          >
            {t('modules')}
          </InputLabel>
          <Select
            multiple
            value={moduleFilter}
            label={t('modules')}
            onChange={handleModuleSelect}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxHeight: 28, overflow: 'hidden' }}>
                {selected.includes('ALL') ? (
                  <Chip size="small"
                    label={t('all')} />
                ) : (
                  selected.slice(0, 2).map((value) => (
                    <Chip key={value}
                      size="small"
                      label={t(transformForTranslation(value))} />
                  ))
                )}
                {!selected.includes('ALL') && selected.length > 2 && (
                  <Chip size="small"
                    label={`+${selected.length - 2}`} />
                )}
              </Box>
            )}
          >
            <MenuItem key='all-modules'
              value="ALL">
              <Checkbox checked={moduleFilter.includes('ALL')} />
              <ListItemText primary={t('all')} />
            </MenuItem>
            {props.modulePermissions.map(mp => (
              <MenuItem key={mp.module}
                value={mp.module}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  {t(transformForTranslation(mp.module || ''))}
                  {mp.premium && (
                    <Trophy01 style={{ width: 16, height: 16, color: theme.palette.warning.main }} />
                  )}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          disabled={searchTerm === '' && moduleFilter.length === 0}
          variant="secondary"
          size="lg"
          onClick={() => handleClear()}
          sx={{ width: '100px' }}
        >
          {t('clearFilters')}
        </Button>
      </Box>
      {props.isLoading
        ? <Skeleton sx={{ bgcolor: theme.palette.custom.gray[100] }}
          variant='rectangular'
          height={445.89} />
        : transformedData.length == 0
          ?
          <EmptyTable
            text={t('roles')}
            searchTerm={searchTerm}
            onClear={handleClear}
            onAdd={() => props.onCreate()}
            addItemText={t('newRole')}
            onAddIsCreating={false}
            withPaginationShown={true}
            headerHeight={55}
            bodyRowHeight={40}
          />
          :
          <RolesTable
            personas={transformedData}
            loadData={props.loadData}
            modules={props.modulePermissions}
          />
      }
    </Grid>
  );
};

export default RolesBox;